import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { API } from "@/App";
import { ArrowLeft } from "lucide-react";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } 
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cabvalley-ride/artifacts/2vw1iw9l_Untitled%20design-14.png";

export default function PolicyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const res = await safeFetch(`${API}/policies/public`);
        if (res.ok) {
          const policies = await res.json();
          setPolicy(policies[slug]);
        }
      } catch (e) { console.error("Failed to fetch policy:", e); }
      setLoading(false);
    };
    fetchPolicy();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-forest border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">Policy Not Found</h1>
        <button
          onClick={() => navigate("/")}
          className="text-forest hover:underline flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-forest/60 hover:text-forest text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <img src={LOGO_URL} alt="Cab Valley" className="h-8 w-auto" />
          <div className="w-16" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-8">
          {policy.title}
        </h1>
        <div className="prose prose-slate max-w-none">
          <div 
            className="whitespace-pre-wrap text-slate-600 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(policy.content) }}
          />
        </div>
        <p className="mt-8 text-sm text-slate-400">
          Last updated: {policy.updated_at ? new Date(policy.updated_at).toLocaleDateString() : "N/A"}
        </p>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-slate-400">
          © {new Date().getFullYear()} Cab Valley. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
